import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, CircularProgress, Grid, IconButton, Typography, Chip, Tooltip, FormControl, InputLabel, Select, MenuItem, Stack, Button, Link, Switch, FormControlLabel, Accordion, AccordionSummary, AccordionDetails, Divider } from '@mui/material';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;

const getJwt = () => localStorage.getItem('feathers-jwt');
const LS_KEY_LEGA = 'giornate.filtro.legaId';

// Parsing YYYY-MM-DD senza timezone per evitare slittamenti di giorno
function parseDateOnly(str) {
  if (!str) return null;
  // atteso formato 'YYYY-MM-DD'
  const [y, m, d] = String(str).split('-').map(n => parseInt(n, 10));
  if (!y || !m || !d) return new Date(str); // fallback
  return new Date(y, m - 1, d);
}

function startOfMonth(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), 1);
  return d;
}

function endOfMonth(date) {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return d;
}

function startOfCalendarGrid(date) {
  // Lunedì come inizio settimana
  const d = new Date(startOfMonth(date));
  const day = (d.getDay() + 6) % 7; // 0=Mon ... 6=Sun
  d.setDate(d.getDate() - day);
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function inRange(date, start, end) {
  const t = new Date(date.setHours(0, 0, 0, 0)).getTime();
  const s = new Date(new Date(start).setHours(0, 0, 0, 0)).getTime();
  const e = new Date(new Date(end).setHours(0, 0, 0, 0)).getTime();
  return t >= s && t <= e;
}

function formatDateYYYYMMDD(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getStartEndGiornata(g) {
  // Supporta varianti di nome campo: dataInizio/dataFine, data_inizio/data_fine
  const diRaw = g.dataInizio ?? g.data_inizio ?? g.datainizio ?? null;
  const dfRaw = g.dataFine ?? g.data_fine ?? g.datafine ?? null;
  const s = parseDateOnly(diRaw) || parseDateOnly(dfRaw) || null;
  const e = parseDateOnly(dfRaw) || parseDateOnly(diRaw) || s;
  return { s, e };
}

export default function GiornateCalendarPage() {
  const [current, setCurrent] = useState(new Date());
  const [giornate, setGiornate] = useState([]);
  const [loading, setLoading] = useState(false);
  const [partiteByDay, setPartiteByDay] = useState(new Map());
  const [leghe, setLeghe] = useState([]);
  const [legaId, setLegaId] = useState('');
  const [teamsMap, setTeamsMap] = useState(new Map());
  const [legheMap, setLegheMap] = useState(new Map());
  const [showAllMonthGiornate, setShowAllMonthGiornate] = useState(false);
  const [calendarExpanded, setCalendarExpanded] = useState(true);
  const notify = useNotify();
  const navigate = useNavigate();

  const start = useMemo(() => startOfMonth(current), [current]);
  const end = useMemo(() => endOfMonth(current), [current]);

  const loadGiornate = async () => {
    try {
      setLoading(true);
      const jwt = getJwt();
      let items = [];

      // Se ho filtro lega uso la procedura; altrimenti fallback a tbl-giornata
      try {
        const paramsProc = {
          p_input_legaId_dal: showAllMonthGiornate ? null : (legaId || null),
          p_input_legaId_al: showAllMonthGiornate ? null : (legaId || null)
        };
        const respProc = await axios.get(`${host}/proc-get-giornata-elenco`, {
          headers: { Authorization: `Bearer ${jwt}` },
          params: paramsProc
        });
        items = respProc.data?.data || [];
      } catch (e) {
        // Fallback a tbl-giornata standard
        const params = {
          $limit: 500,
          'dataInizio[$lte]': end.toISOString().slice(0, 10)
        };
        const resp = await axios.get(`${host}/tbl-giornata`, {
          headers: { Authorization: `Bearer ${jwt}` },
          params
        });
        items = resp.data?.data || [];
      }
      // Filtro client: una giornata è nel mese se l'intervallo [dataInizio, dataFine||dataInizio] interseca [start,end]
      const filtered = items.filter(g => {
        const di = parseDateOnly(g.dataInizio) || parseDateOnly(g.dataFine) || null;
        const df = parseDateOnly(g.dataFine) || parseDateOnly(g.dataInizio) || null;
        if (!di && !df) return false;
        const sDate = di || df;
        const eDate = df || di;
        return !(eDate < start || sDate > end);
      });
      setGiornate(filtered);
    } catch (e) {
      console.error('Errore nel caricamento giornate', e);
      notify('Errore nel caricamento delle giornate', { type: 'error' });
      setGiornate([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPartite = async () => {
    try {
      const jwt = getJwt();
      const params = {
        $limit: 1000,
        'data[$gte]': start.toISOString().slice(0, 10),
        'data[$lte]': end.toISOString().slice(0, 10),
        $sort: { data: 1 },
        ...(legaId ? { legaId } : {})
      };
      const resp = await axios.get(`${host}/tbl-partita`, {
        headers: { Authorization: `Bearer ${jwt}` },
        params
      });
      const items = resp.data?.data || [];
      const map = new Map();
      items.forEach(p => {
        const d = parseDateOnly(p.data);
        if (!d) return;
        const key = d.toDateString();
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(p);
      });
      setPartiteByDay(map);
    } catch (e) {
      console.error('Errore nel caricamento partite', e);
      // Non blocchiamo la pagina per errore sulle partite
    }
  };

  useEffect(() => {
    loadGiornate();
    loadPartite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, legaId, showAllMonthGiornate]);

  // Carica leghe e stagioni per i filtri
  useEffect(() => {
    // Inizializza filtri da localStorage
    const savedLega = localStorage.getItem(LS_KEY_LEGA);
    if (savedLega !== null) setLegaId(savedLega);

    (async () => {
      try {
        const jwt = getJwt();
        const [legheResp, teamsResp] = await Promise.all([
          axios.get(`${host}/tbl-lega`, { headers: { Authorization: `Bearer ${jwt}` }, params: { $limit: 200, $sort: { nome: 1 } } }),
          axios.get(`${host}/tbl-team`, { headers: { Authorization: `Bearer ${jwt}` }, params: { $limit: 1000, $sort: { nome: 1 } } })
        ]);
        const legheData = legheResp.data?.data || [];
        setLeghe(legheData);

        // Crea mappa delle leghe
        const lmap = new Map();
        legheData.forEach(l => lmap.set(l.id, l.nome));
        setLegheMap(lmap);

        const tmap = new Map();
        (teamsResp.data?.data || []).forEach(t => tmap.set(t.id, t.nome));
        setTeamsMap(tmap);
      } catch (e) {
        console.error('Errore caricamento filtri', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gridStart = useMemo(() => startOfCalendarGrid(current), [current]);
  const days = useMemo(() => Array.from({ length: 42 }, (_, i) => addDays(gridStart, i)), [gridStart]);

  const monthTitle = useMemo(() => current.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }), [current]);
  const monthOptions = useMemo(() => ([
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ]), []);
  const yearOptions = useMemo(() => {
    const cy = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => cy - 5 + i);
  }, []);

  const dayGiornateMap = useMemo(() => {
    const map = new Map();
    days.forEach(d => map.set(d.toDateString(), []));
    giornate.forEach(g => {
      const { s, e } = getStartEndGiornata(g);
      const sEff = s || new Date();
      const eEff = e || sEff;
      days.forEach(d => {
        if (inRange(new Date(d), sEff, eEff)) {
          map.get(d.toDateString()).push(g);
        }
      });
    });
    return map;
  }, [days, giornate]);

  // Deriva la lista giornate effettivamente visibili nel mese dal dayGiornateMap
  const monthGiornate = useMemo(() => {
    const set = new Map(); // usa Map per preservare inserimento e velocità
    dayGiornateMap.forEach(arr => {
      arr.forEach(g => {
        if (!set.has(g.id)) set.set(g.id, g);
      });
    });
    return Array.from(set.values());
  }, [dayGiornateMap]);

  // Helper: tutte le partite del mese in un array
  const allPartiteOfMonth = useMemo(() => {
    const arr = [];
    partiteByDay.forEach(val => {
      if (Array.isArray(val)) arr.push(...val);
      else if (val && Array.isArray(val.data)) arr.push(...val.data);
    });
    return arr;
  }, [partiteByDay]);

  // Fallback: se non abbiamo giornate visibili, derivale dalle partite per giornataId
  const monthGiornateDerivedFromPartite = useMemo(() => {
    if (monthGiornate.length > 0) return [];
    const byG = new Map();
    allPartiteOfMonth.forEach(p => {
      if (p.giornataId == null) return;
      const d = parseDateOnly(p.data);
      if (!byG.has(p.giornataId)) byG.set(p.giornataId, { id: p.giornataId, numero: p.giornataId, s: d, e: d });
      const obj = byG.get(p.giornataId);
      if (d && (!obj.s || d < obj.s)) obj.s = d;
      if (d && (!obj.e || d > obj.e)) obj.e = d;
    });
    // Converte in shape simile a giornate
    return Array.from(byG.values()).map(x => ({ id: x.id, numero: x.numero, dataInizio: formatDateYYYYMMDD(x.s), dataFine: formatDateYYYYMMDD(x.e), nome: x.nome, flDisputata: false }));
  }, [allPartiteOfMonth, monthGiornate.length]);

  // Prova a caricare dettagli reali giornate se abbiamo solo partite (per migliorare chips anche nel calendario)
  useEffect(() => {
    (async () => {
      if (giornate.length === 0 && allPartiteOfMonth.length > 0) {
        const ids = Array.from(new Set(allPartiteOfMonth.map(p => p.giornataId).filter(Boolean)));
        if (ids.length > 0) {
          try {
            const jwt = getJwt();
            const resp = await axios.get(`${host}/tbl-giornata`, {
              headers: { Authorization: `Bearer ${jwt}` },
              params: { $limit: 500, id: { $in: ids } }
            });
            const items = resp.data?.data || [];
            if (items.length > 0) setGiornate(items);
          } catch (e) {
            // Ignora errori: manterremo il fallback in lista
          }
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPartiteOfMonth]);

  // Lista finale giornate da mostrare sotto
  const finalMonthGiornate = useMemo(() => (
    monthGiornate.length > 0 ? monthGiornate : monthGiornateDerivedFromPartite
  ), [monthGiornate, monthGiornateDerivedFromPartite]);

  const goPrev = () => setCurrent(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const goNext = () => setCurrent(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const goToday = () => setCurrent(new Date());

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarTodayIcon color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{monthTitle}</Typography>
            </Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="mese-label">Mese</InputLabel>
                <Select labelId="mese-label" label="Mese" value={current.getMonth()} onChange={(e) => setCurrent(d => new Date(d.getFullYear(), Number(e.target.value), 1))}>
                  {monthOptions.map((m, idx) => (
                    <MenuItem key={m} value={idx}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="anno-label">Anno</InputLabel>
                <Select labelId="anno-label" label="Anno" value={current.getFullYear()} onChange={(e) => setCurrent(d => new Date(Number(e.target.value), d.getMonth(), 1))}>
                  {yearOptions.map(y => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="lega-label">Lega</InputLabel>
                <Select labelId="lega-label" label="Lega" value={legaId} onChange={(e) => { setLegaId(e.target.value); localStorage.setItem(LS_KEY_LEGA, e.target.value); }}>
                  <MenuItem value=""><em>Tutte</em></MenuItem>
                  {leghe.map(l => (
                    <MenuItem key={l.id} value={l.id}>{l.nome}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <Tooltip title="Mese precedente"><IconButton onClick={goPrev}><ArrowBackIosNewIcon /></IconButton></Tooltip>
                <Tooltip title="Oggi"><IconButton onClick={goToday}><EventAvailableIcon /></IconButton></Tooltip>
                <Tooltip title="Mese successivo"><IconButton onClick={goNext}><ArrowForwardIosIcon /></IconButton></Tooltip>
              </Box>
            </Stack>
          </Box>

          <Accordion expanded={calendarExpanded} onChange={() => setCalendarExpanded(prev => !prev)} disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={calendarExpanded ? <RemoveIcon /> : <AddIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Calendario mese</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(d => (
                      <Grid item xs={12 / 7} key={d} sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 700 }}>{d}</Typography>
                      </Grid>
                    ))}
                  </Grid>

                  <Grid container spacing={1}>
                    {days.map((day, idx) => {
                      const inCurrentMonth = day.getMonth() === current.getMonth();
                      const list = dayGiornateMap.get(day.toDateString()) || [];
                      const rawPartite = partiteByDay.get(day.toDateString());
                      const partite = Array.isArray(rawPartite) ? rawPartite : (rawPartite && Array.isArray(rawPartite.data) ? rawPartite.data : []);
                      return (
                        <Grid item xs={12 / 7} key={idx}>
                          <Box sx={{
                            border: '1px solid',
                            borderColor: inCurrentMonth ? (list.length > 0 ? 'primary.light' : '#e0e0e0') : '#f0f0f0',
                            bgcolor: inCurrentMonth ? (list.length > 0 ? 'primary.lighter' : 'white') : '#fafafa',
                            borderRadius: 2,
                            minHeight: 140,
                            p: 1.5,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              boxShadow: 2,
                              borderColor: 'primary.main'
                            }
                          }}>
                            {/* Header del giorno */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="h6" sx={{
                                fontWeight: 700,
                                color: inCurrentMonth ? 'text.primary' : 'text.disabled',
                                fontSize: '1.2rem'
                              }}>
                                {day.getDate()}
                              </Typography>
                              {isSameDay(day, new Date()) && (
                                <Chip size="small" label="Oggi" color="primary" variant="filled" sx={{ fontSize: '0.8rem' }} />
                              )}
                            </Box>

                            {/* Contenuto del giorno */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                              {/* Giornate */}
                              {list.slice(0, 2).map(g => (
                                <Box key={g.id} sx={{ mb: 0.5 }}>
                                  <Tooltip
                                    title={
                                      <Box sx={{ p: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                          {g.nome || `Giornata ${g.nome || ''}`}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                          📅 Dal: {g.dataInizio || '-'} Al: {g.dataFine || g.dataInizio || '-'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                          🏆 Stato: {g.flDisputata ? 'Disputata' : 'Da disputare'}
                                        </Typography>
                                      </Box>
                                    }
                                    arrow
                                    placement="top"
                                  >
                                    <Box
                                      onClick={() => navigate(`/giornate/${g.id}`)}
                                      sx={{
                                        cursor: 'pointer',
                                        p: 0.75,
                                        borderRadius: 1,
                                        bgcolor: g.flDisputata ? 'success.lighter' : 'warning.lighter',
                                        border: '1px solid',
                                        borderColor: g.flDisputata ? 'success.main' : 'warning.main',
                                        '&:hover': {
                                          bgcolor: g.flDisputata ? 'success.light' : 'warning.light',
                                          transform: 'scale(1.02)'
                                        },
                                        transition: 'all 0.2s ease-in-out'
                                      }}
                                    >
                                      {/* Nome Lega */}
                                      {g.legaId && legheMap.get(g.legaId) && (
                                        <Typography variant="caption" sx={{
                                          fontWeight: 600,
                                          color: g.flDisputata ? 'success.dark' : 'warning.dark',
                                          display: 'block',
                                          textAlign: 'center',
                                          fontSize: '0.7rem',
                                          opacity: 0.8,
                                          mb: 0.25
                                        }}>
                                          {legheMap.get(g.legaId)}
                                        </Typography>
                                      )}

                                      {/* Giornata numero */}
                                      <Typography variant="body2" sx={{
                                        fontWeight: 800,
                                        color: g.flDisputata ? 'success.dark' : 'warning.dark',
                                        display: 'block',
                                        textAlign: 'center',
                                        fontSize: '0.9rem'
                                      }}>
                                        Giornata: {g.numero || g.id}
                                      </Typography>

                                      {/* Stato */}
                                      <Typography variant="caption" sx={{
                                        color: g.flDisputata ? 'success.dark' : 'warning.dark',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.5,
                                        mt: 0.25,
                                        fontSize: '0.75rem'
                                      }}>
                                        {g.flDisputata ? <EventAvailableIcon sx={{ fontSize: 14 }} /> : <PendingActionsIcon sx={{ fontSize: 14 }} />}
                                        {g.flDisputata ? 'Disputata' : 'Da disputare'}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                </Box>
                              ))}

                              {list.length > 2 && (
                                <Typography variant="body2" color="text.secondary" sx={{
                                  textAlign: 'center',
                                  fontStyle: 'italic',
                                  p: 0.75,
                                  bgcolor: 'grey.100',
                                  borderRadius: 1,
                                  fontSize: '0.8rem',
                                  fontWeight: 600
                                }}>
                                  +{list.length - 2} altre giornate
                                </Typography>
                              )}

                              {/* Separatore se ci sono sia giornate che partite */}
                              {list.length > 0 && partite.length > 0 && (
                                <Divider sx={{ my: 0.5 }} />
                              )}

                              {/* Partite */}
                              {partite.length > 0 && (
                                <Box sx={{
                                  p: 0.75,
                                  bgcolor: 'info.lighter',
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'info.light'
                                }}>
                                  <Typography variant="body2" sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    fontWeight: 800,
                                    color: 'info.dark',
                                    mb: 0.5,
                                    fontSize: '0.85rem'
                                  }}>
                                    <SportsSoccerIcon sx={{ fontSize: 16 }} />
                                    Partite: {partite.length}
                                  </Typography>

                                  {/* Leghe delle partite */}
                                  {(() => {
                                    const leghePartite = [...new Set(partite.map(p => p.legaId).filter(Boolean))];
                                    if (leghePartite.length > 0) {
                                      return (
                                        <Typography variant="caption" sx={{
                                          display: 'block',
                                          color: 'info.dark',
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                          mb: 0.5,
                                          opacity: 0.9
                                        }}>
                                          {leghePartite.length === 1
                                            ? `Lega: ${legheMap.get(leghePartite[0]) || `ID ${leghePartite[0]}`}`
                                            : `Leghe: ${leghePartite.map(id => legheMap.get(id) || `ID ${id}`).join(', ')}`
                                          }
                                        </Typography>
                                      );
                                    }
                                    return null;
                                  })()}

                                  {/* Anteprima partite (max 1 nel calendario) */}
                                  {partite.slice(0, 1).map(p => (
                                    <Typography key={p.id} variant="body2" sx={{
                                      display: 'block',
                                      color: 'info.dark',
                                      fontSize: '0.75rem',
                                      fontWeight: 500
                                    }}>
                                      <Link
                                        component="button"
                                        underline="hover"
                                        onClick={() => navigate(`/partite/${p.id}`)}
                                        sx={{ color: 'info.dark', fontSize: 'inherit', fontWeight: 'inherit' }}
                                      >
                                        {(teamsMap.get(p.teamCasaId) || `Team ${p.teamCasaId}`)} vs {(teamsMap.get(p.teamOspiteId) || `Team ${p.teamOspiteId}`)}
                                        {p.ore ? ` • ${p.ore.substring(0, 5)}` : ''}
                                      </Link>
                                    </Typography>
                                  ))}

                                  {partite.length > 1 && (
                                    <Typography variant="body2" sx={{
                                      color: 'info.dark',
                                      fontStyle: 'italic',
                                      fontSize: '0.75rem',
                                      fontWeight: 500
                                    }}>
                                      … e altre {partite.length - 1}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Lista giornate del mese */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Giornate del mese
                <Typography component="span" variant="subtitle2" sx={{ ml: 1, color: 'text.secondary' }}>({finalMonthGiornate.length})</Typography>
              </Typography>
              <FormControlLabel
                control={<Switch checked={showAllMonthGiornate} onChange={(e) => setShowAllMonthGiornate(e.target.checked)} />}
                label="Mostra tutte le giornate (ignora filtri)"
              />
            </Box>
            {finalMonthGiornate
              .slice()
              .sort((a, b) => {
                const { s: aStart } = getStartEndGiornata(a);
                const { s: bStart } = getStartEndGiornata(b);
                const aS = aStart || new Date(0);
                const bS = bStart || new Date(0);
                if (aS.getTime() !== bS.getTime()) return aS - bS;
                return (a.numero || 0) - (b.numero || 0);
              })
              .map(g => {
                const { s, e } = getStartEndGiornata(g);
                // Conteggio partite nel range della giornata
                let partiteCount = 0;
                if (s) {
                  const cursor = new Date(s);
                  while (cursor <= e) {
                    const key = cursor.toDateString();
                    const arr = partiteByDay.get(key) || [];
                    partiteCount += arr.length;
                    cursor.setDate(cursor.getDate() + 1);
                  }
                }

                const sStr = formatDateYYYYMMDD(s);
                const eStr = formatDateYYYYMMDD(e);

                return (
                  <Card key={g.id} sx={{ mb: 1, borderRadius: 2, borderLeft: `4px solid ${g.flDisputata ? '#2e7d32' : '#ed6c02'}` }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {g.nome || `Giornata ${g.nome || ''}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {sStr} {eStr && eStr !== sStr ? `→ ${eStr}` : ''}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" label={g.flDisputata ? 'Disputata' : 'Da disputare'} color={g.flDisputata ? 'success' : 'warning'} />
                            {typeof g.numero !== 'undefined' && <Chip size="small" label={`N° ${g.numero}`} />}
                            <Chip size="small" label={`Partite: ${partiteCount}`} />
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                            <Button size="small" variant="outlined" onClick={() => navigate(`/giornate/${g.id}`)}>Dettaglio</Button>
                          </Stack>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })}
            {finalMonthGiornate.length === 0 && (
              <Typography variant="body2" color="text.secondary">Nessuna giornata nel mese corrente.</Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
